import { decode } from '@googlemaps/polyline-codec';
import { layers, namedFlavor } from '@protomaps/basemaps';
import type { StyleSpecification } from 'maplibre-gl';

import type { LngLat } from './bus-motion';

// rushowl-hosted Protomaps Basemap (planet, MVT, zoom 0–14). One source of
// truth — ported from RushOS lib/map-style.ts so both apps can swap the tile
// provider in one place if needed.
const RUSHOWL_TILES_URL = 'https://tiles.rushowl.app/output.json';

// Protomaps' hosted basemap assets. Fine for dev; for prod we can later
// self-host these to drop the third-party CDN dependency.
const PROTOMAPS_GLYPHS = 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf';
const PROTOMAPS_SPRITES_BASE = 'https://protomaps.github.io/basemaps-assets/sprites/v4';

const SOURCE_ID = 'protomaps';

type Flavor = 'light' | 'dark' | 'white' | 'black' | 'grayscale';

/**
 * Build a MapLibre style JSON that renders the rushowl-hosted Protomaps
 * Basemap tileset. `flavor` selects one of Protomaps' built-in cartographic
 * themes; this portal only uses `light` (no dark mode).
 */
export function protomapsStyle(flavor: Flavor = 'light'): StyleSpecification {
  return {
    version: 8,
    glyphs: PROTOMAPS_GLYPHS,
    sprite: `${PROTOMAPS_SPRITES_BASE}/${flavor}`,
    sources: {
      [SOURCE_ID]: {
        type: 'vector',
        url: RUSHOWL_TILES_URL,
        attribution:
          '&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> &middot; <a href="https://protomaps.com">Protomaps</a>',
      },
    },
    layers: layers(SOURCE_ID, namedFlavor(flavor), { lang: 'en' }),
  } as StyleSpecification;
}

// Overlay polyline colors, shared by the gmap line layers and the map
// legend so the swatches always match what's drawn. Planned route uses the
// timeline's passed-gray (ro-passed), the live socket ETA path uses the
// sheet timeline's brand blue (ro-blue).
export const PLANNED_PATH_COLOR = '#9ca3af';
export const LIVE_PATH_COLOR = '#4285f4';

type LineStringFeature = {
  type: 'Feature';
  properties: Record<string, never>;
  geometry: { type: 'LineString'; coordinates: [number, number][] };
};

export type LineFeatureCollection = {
  type: 'FeatureCollection';
  features: LineStringFeature[];
};

/**
 * Decode Google-encoded polylines (the backend's trip/socket path format)
 * into a GeoJSON FeatureCollection of LineStrings. The codec yields
 * [lat, lng] pairs; GeoJSON wants [lng, lat], so the seam flips them.
 * Undefined, empty, and single-point paths are skipped — they cannot form
 * a line.
 */
export function encodedPathsToFeatureCollection(
  paths: (string | undefined)[]
): LineFeatureCollection {
  const features: LineStringFeature[] = [];
  paths.forEach((path) => {
    if (!path) return;
    const points = decode(path);
    if (points.length < 2) return;
    features.push({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: points.map(([lat, lng]) => [lng, lat]),
      },
    });
  });
  return { type: 'FeatureCollection', features };
}

/**
 * Decode and concatenate encoded polylines into one [lng, lat] path.
 * The codec yields [lat, lng]; this is the seam where that flips.
 * Consecutive duplicate points are dropped so segment lengths are never zero.
 */
export function encodedPathsToLngLats(paths: (string | undefined)[]): LngLat[] {
  const out: LngLat[] = [];
  paths.forEach((path) => {
    if (!path) return;
    decode(path).forEach(([lat, lng]) => {
      const prev = out[out.length - 1];
      if (prev && prev[0] === lng && prev[1] === lat) return;
      out.push([lng, lat]);
    });
  });
  return out;
}
